import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Modal, Form, InputNumber,
    Select, Space, Tag, message, Row, Col, Progress,
    Input, Tooltip
} from 'antd';
import {
    EditOutlined, ReloadOutlined, UserOutlined,
    SearchOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';

const SectionCard = styled(Card)`
    margin-bottom: 16px;
`;

const FilterContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const StatCard = styled.div`
    background: #f5f5f5;
    border-radius: 8px;
    padding: 16px;
    text-align: center;

    .stat-value {
        font-size: 24px;
        font-weight: 600;
        color: #002C57;
    }

    .stat-label {
        color: #666;
        font-size: 13px;
        margin-top: 4px;
    }
`;

const formatTokens = (tokens) => {
    if (!tokens) return '0';
    if (tokens >= 1000000) {
        return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
        return `${(tokens / 1000).toFixed(0)}K`;
    }
    return tokens.toLocaleString();
};

const UserTokenManagement = () => {
    const [customers, setCustomers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [userQuotas, setUserQuotas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [bulkEditModalVisible, setBulkEditModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [form] = Form.useForm();
    const [bulkForm] = Form.useForm();

    const fetchOrganizations = async () => {
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/organizations`
            );
            if (response.success) {
                setOrganizations(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch organizations:', error);
        }
    };

    const fetchCustomers = async (orgId) => {
        if (!orgId) {
            setCustomers([]);
            return;
        }
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/customers?orgId=${orgId}`
            );
            if (response.success) {
                setCustomers(response.data || []);
            }
        } catch (error) {
            console.error('Failed to fetch customers:', error);
        }
    };

    const fetchUserQuotas = async () => {
        if (!selectedCustomer) return;

        try {
            setLoading(true);
            const yearMonth = dayjs().format('YYYY-MM');
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/user-token-limits?customerId=${selectedCustomer}&yearMonth=${yearMonth}`
            );
            if (response.success) {
                setUserQuotas(response.data);
            }
        } catch (error) {
            message.error('Failed to fetch user quotas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    useEffect(() => {
        if (selectedCustomer) {
            fetchUserQuotas();
        }
    }, [selectedCustomer]);

    const handleEditUser = (user) => {
        setSelectedUser(user);
        form.setFieldsValue({
            tokenLimitMonthly: user.TokenLimitMonthly
        });
        setEditModalVisible(true);
    };

    const handleSaveUserLimit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const response = await execute(
                'PUT',
                `${protectedResources.api.baseEndPoint}/ai-subscription/user-token-limits/${selectedUser.CustomerUserId}`,
                { tokenLimit: values.tokenLimitMonthly }
            );

            if (response.success) {
                message.success('Token limit updated successfully');
                setEditModalVisible(false);
                fetchUserQuotas();
            } else {
                message.error(response.message || 'Failed to update token limit');
            }
        } catch (error) {
            message.error('Failed to update token limit');
        } finally {
            setLoading(false);
        }
    };

    const handleBulkUpdate = async () => {
        try {
            const values = await bulkForm.validateFields();
            setLoading(true);

            // Update each user's token limit
            const updatePromises = userQuotas.map(user =>
                execute(
                    'PUT',
                    `${protectedResources.api.baseEndPoint}/ai-subscription/user-token-limits/${user.CustomerUserId}`,
                    { tokenLimit: values.tokenLimitMonthly }
                )
            );

            await Promise.all(updatePromises);
            message.success('All token limits updated successfully');
            setBulkEditModalVisible(false);
            fetchUserQuotas();
        } catch (error) {
            message.error('Failed to update token limits');
        } finally {
            setLoading(false);
        }
    };

    const getStatusTag = (status) => {
        const config = {
            Normal: { color: 'green', text: 'Normal' },
            Warn70: { color: 'orange', text: 'Warning (70%)' },
            Warn90: { color: 'red', text: 'Warning (90%)' },
            Blocked: { color: 'error', text: 'Blocked' }
        };
        const { color, text } = config[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
    };

    const getUsagePercentage = (consumed, limit) => {
        if (!limit) return 0;
        return Math.round((consumed / limit) * 100);
    };

    const getProgressColor = (percentage) => {
        if (percentage >= 100) return '#f5222d';
        if (percentage >= 90) return '#fa8c16';
        if (percentage >= 70) return '#faad14';
        return '#52c41a';
    };

    const columns = [
        {
            title: 'User',
            key: 'user',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <div>
                        <div style={{ fontWeight: 500 }}>{record.FirstName} {record.LastName}</div>
                        <div style={{ fontSize: 12, color: '#999' }}>{record.Email}</div>
                    </div>
                </Space>
            ),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search user"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={confirm}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button type="primary" onClick={confirm} size="small" icon={<SearchOutlined />}>
                            Search
                        </Button>
                        <Button onClick={clearFilters} size="small">Reset</Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) => {
                const searchValue = value.toLowerCase();
                return (
                    record.Email?.toLowerCase().includes(searchValue) ||
                    record.FirstName?.toLowerCase().includes(searchValue) ||
                    record.LastName?.toLowerCase().includes(searchValue)
                );
            },
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        },
        {
            title: 'Token Limit',
            dataIndex: 'TokenLimitMonthly',
            key: 'tokenLimit',
            render: (val) => formatTokens(val),
            sorter: (a, b) => a.TokenLimitMonthly - b.TokenLimitMonthly
        },
        {
            title: 'Tokens Used',
            dataIndex: 'TokensConsumed',
            key: 'tokensConsumed',
            render: (val) => formatTokens(val),
            sorter: (a, b) => a.TokensConsumed - b.TokensConsumed
        },
        {
            title: 'Usage',
            key: 'usage',
            render: (_, record) => {
                const percentage = getUsagePercentage(record.TokensConsumed, record.TokenLimitMonthly);
                return (
                    <Tooltip title={`${percentage}% used`}>
                        <Progress
                            percent={percentage}
                            size="small"
                            strokeColor={getProgressColor(percentage)}
                            status={percentage >= 100 ? 'exception' : 'active'}
                            style={{ width: 100 }}
                        />
                    </Tooltip>
                );
            },
            sorter: (a, b) => {
                const percA = getUsagePercentage(a.TokensConsumed, a.TokenLimitMonthly);
                const percB = getUsagePercentage(b.TokensConsumed, b.TokenLimitMonthly);
                return percA - percB;
            }
        },
        {
            title: 'Remaining',
            key: 'remaining',
            render: (_, record) => {
                const remaining = Math.max(0, (record.TokenLimitMonthly || 0) - (record.TokensConsumed || 0));
                return formatTokens(remaining);
            }
        },
        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'status',
            render: (status) => getStatusTag(status),
            filters: [
                { text: 'Normal', value: 'Normal' },
                { text: 'Warning (70%)', value: 'Warn70' },
                { text: 'Warning (90%)', value: 'Warn90' },
                { text: 'Blocked', value: 'Blocked' }
            ],
            onFilter: (value, record) => record.Status === value
        },
        {
            title: 'Override',
            dataIndex: 'IsManualOverride',
            key: 'override',
            render: (val) => val ? <Tag color="purple">Manual</Tag> : <Tag>Default</Tag>
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => handleEditUser(record)}
                >
                    Edit
                </Button>
            )
        }
    ];

    const stats = {
        total: userQuotas.length,
        normal: userQuotas.filter(u => u.Status === 'Normal').length,
        warning: userQuotas.filter(u => u.Status === 'Warn70' || u.Status === 'Warn90').length,
        blocked: userQuotas.filter(u => u.Status === 'Blocked').length
    };

    return (
        <div>
            <SectionCard title="User Token Management">
                <FilterContainer>
                    <Select
                        placeholder="Select Organization"
                        style={{ width: 250 }}
                        showSearch
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(val) => {
                            setSelectedOrg(val);
                            setSelectedCustomer(null);
                            fetchCustomers(val);
                        }}
                        options={organizations.map(org => ({
                            value: org.Org_ID,
                            label: org.Org_Name
                        }))}
                    />
                    <Select
                        placeholder="Select Customer"
                        style={{ width: 250 }}
                        showSearch
                        disabled={!selectedOrg}
                        value={selectedCustomer}
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(val) => setSelectedCustomer(val)}
                        options={customers.map(cust => ({
                            value: cust.Customer_ID,
                            label: cust.CustomerName
                        }))}
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchUserQuotas}
                        loading={loading}
                        disabled={!selectedCustomer}
                    >
                        Refresh
                    </Button>
                    {userQuotas.length > 0 && (
                        <Button
                            type="primary"
                            onClick={() => {
                                bulkForm.resetFields();
                                setBulkEditModalVisible(true);
                            }}
                        >
                            Bulk Update Limits
                        </Button>
                    )}
                </FilterContainer>

                {selectedCustomer && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col span={6}>
                            <StatCard>
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">Total Users</div>
                            </StatCard>
                        </Col>
                        <Col span={6}>
                            <StatCard>
                                <div className="stat-value" style={{ color: '#52c41a' }}>{stats.normal}</div>
                                <div className="stat-label">Normal</div>
                            </StatCard>
                        </Col>
                        <Col span={6}>
                            <StatCard>
                                <div className="stat-value" style={{ color: '#faad14' }}>{stats.warning}</div>
                                <div className="stat-label">Warning</div>
                            </StatCard>
                        </Col>
                        <Col span={6}>
                            <StatCard>
                                <div className="stat-value" style={{ color: '#f5222d' }}>{stats.blocked}</div>
                                <div className="stat-label">Blocked</div>
                            </StatCard>
                        </Col>
                    </Row>
                )}

                <Table
                    dataSource={userQuotas}
                    columns={columns}
                    rowKey="UserQuotaId"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{
                        emptyText: selectedCustomer
                            ? 'No user quotas found'
                            : 'Select an organization and customer to view user quotas'
                    }}
                />
            </SectionCard>

            {/* Edit User Token Limit Modal */}
            <Modal
                title={`Edit Token Limit - ${selectedUser?.Email}`}
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                onOk={handleSaveUserLimit}
                confirmLoading={loading}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="tokenLimitMonthly"
                        label="Monthly Token Limit"
                        rules={[{ required: true, message: 'Please enter token limit' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                    {selectedUser && (
                        <div style={{ color: '#666', fontSize: 13 }}>
                            <p>Current Usage: {formatTokens(selectedUser.TokensConsumed)} tokens</p>
                            <p>Status: {selectedUser.Status}</p>
                        </div>
                    )}
                </Form>
            </Modal>

            {/* Bulk Edit Modal */}
            <Modal
                title="Bulk Update Token Limits"
                open={bulkEditModalVisible}
                onCancel={() => setBulkEditModalVisible(false)}
                onOk={handleBulkUpdate}
                confirmLoading={loading}
            >
                <Form form={bulkForm} layout="vertical">
                    <Form.Item
                        name="tokenLimitMonthly"
                        label="New Monthly Token Limit for All Users"
                        rules={[{ required: true, message: 'Please enter token limit' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        />
                    </Form.Item>
                    <div style={{ color: '#fa8c16', fontSize: 13 }}>
                        This will update the token limit for all {userQuotas.length} users in this customer.
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default UserTokenManagement;
